import React, { useState } from 'react';
import {
	Search,
	Phone,
	User,
	Mail,
	Building,
	Calendar,
	Clock,
	Edit,
	X,
	Check,
	Headphones,
	Globe,
} from 'lucide-react';
import { Button, Empty, Input, notification, Popconfirm, Select, Spin } from 'antd';
import { fetchNumbersFn } from '@/api/reservedNumbers';
import { useMutation } from '@tanstack/react-query';
import { agentProvisionedNumberHistory, updateAgentProvisionedNumberAdmin } from '@/api/admin';
import moment from 'moment-timezone';
import { countriesWithFlag } from 'src/constants/countriesData';

interface props {
	onBack: () => void;
}

const AdminPhoneNumbers: React.FC<props> = ({ onBack }) => {
	const [selectedCountry, setSelectedCountry] = useState('US');
	const [selectedAreaCode, setSelectedAreaCode] = useState('');
	const [reason, setReason] = useState('');
	const [clientType, setClientType] = useState('SMB');
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedClient, setSelectedClient] = useState(null);
	const [availableNumbers, setAvailableNumbers] = useState([]);
	const [showChangeModal, setShowChangeModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const [queried, setQueried] = useState<boolean>(false);

	const { mutate, isPending: isPendingAgentHistory } = useMutation({
		mutationKey: ['agent-provisioned-number-history'],
		mutationFn: agentProvisionedNumberHistory,
		onError: (error) => {
			notification.error({ message: `Failed to fetch: ${error.message}` });
		},
		onSuccess: ({ success, data }) => {
			if (success && data) {
				setSelectedClient(data);
				// setSelectedClient(mockClient)
			}
		},
	});

	const { mutate: updateAgentProvisionedNumberAdminFn, isPending: isPendingUpdateNumber } =
		useMutation({
			mutationKey: ['updatePhoneNumberAdmin'],
			mutationFn: updateAgentProvisionedNumberAdmin,
			onError: (error) => {
				notification.error({ message: `Failed to update: ${error.message}` });
			},
			onSuccess: () => {
				notification.success({ message: 'Phone number updated successfully!' });
				mutate(searchQuery);
			},
		});

	const getListOfNumbers = async () => {
		try {
			setLoading(true);
			const response = await fetchNumbersFn(
				selectedCountry,
				selectedAreaCode,
				setLoading,
				setQueried,
			);
			const nums = response?.locals?.map((item: any) => item.phoneNumber);
			setAvailableNumbers(nums);
		} catch (error) {
			console.error(error);
			notification.error({ message: `Failed to fetch numbers: ${error.message}` });
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = () => {
		if (searchQuery.trim()) {
			mutate(searchQuery);
		}
	};

	const handleNumberSelect = (number) => {
		setShowChangeModal(false);
		updateAgentProvisionedNumberAdminFn({
			number: selectedClient?.number,
			phone: number,
			countryCode: selectedCountry,
			updateReason: reason,
			userId: selectedClient?.userId,
		});
	};

	return (
		<div className="min-h-screen bg-gray-50 p-6">
			<Spin spinning={isPendingAgentHistory || isPendingUpdateNumber}>
				<div className="max-w-7xl mx-auto pb-12">
					<div>
						<div className="flex items-center gap-8 mb-8">
							<Button type="text" className="hover:text-blue-500" onClick={onBack}>
								⬅ Back
							</Button>
							<h1 className="text-3xl font-bold text-gray-900">
								Phone Number Management Dashboard
							</h1>
						</div>
					</div>

					{/* Number Search Section */}
					<div className="bg-white rounded-lg shadow-md p-6 mb-8">
						<h2 className="text-xl font-semibold text-gray-800 mb-4">Available Numbers</h2>

						<div className="w-full flex gap-12 items-end mb-6">
							<div className="w-full">
								<label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
								<Select
									value={selectedCountry}
									onChange={(value) => setSelectedCountry(value)}
									className="w-full"
									showSearch
								>
									<Select.Option value="">Select Country</Select.Option>
									{countriesWithFlag.map((country) => (
										<Select.Option key={country.code} value={country.code}>
											{country.flag} {country.name}
										</Select.Option>
									))}
								</Select>
							</div>

							<div className="w-full">
								<label className="block text-sm font-medium text-gray-700 mb-2">Area Code</label>
								<Input
									type="text"
									value={selectedAreaCode}
									onChange={(e) => setSelectedAreaCode(e.target.value.trim())}
									disabled={loading}
									placeholder="Enter Area Code"
									className="w-full"
								/>
							</div>

							<Button
								onClick={() => getListOfNumbers()}
								disabled={loading || selectedAreaCode.length !== 3}
								type="primary"
							>
								<Search className="w-4 h-4" />
								Search
							</Button>
						</div>

						{queried && availableNumbers.length > 0 && (
							<div>
								<h3 className="text-lg font-medium text-gray-800 mb-3">
									Available Numbers ({availableNumbers.length})
								</h3>
								<div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-48 overflow-y-auto">
									{availableNumbers.map((number, index) => (
										<div
											key={index}
											className="p-2 bg-blue-50 rounded border text-center text-sm font-mono"
										>
											{number}
										</div>
									))}
								</div>
							</div>
						)}

						{queried && availableNumbers.length === 0 && (
							<div>
								<h3 className="text-lg font-medium text-gray-800 mb-3">
									Available Numbers ({availableNumbers.length})
								</h3>
								<Empty />
							</div>
						)}
					</div>

					{/* Client Search Section */}
					<div className="bg-white rounded-lg shadow-md p-6 mb-8">
						<h2 className="text-xl font-semibold text-gray-800 mb-4">Client Search</h2>

						<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">Client Type</label>
								<Select
									value={clientType}
									onChange={(value) => setClientType(value)}
									className="w-full"
								>
									<Select.Option value="SMB">SMB</Select.Option>
									<Select.Option value="Reseller" disabled>
										Reseller
									</Select.Option>
								</Select>
							</div>

							<div className="md:col-span-2">
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Search by {clientType === 'SMB' ? 'Username' : 'Email'}
								</label>
								<div className="flex gap-2">
									<Input
										type="text"
										value={searchQuery}
										onChange={(e) => setSearchQuery(e.target.value)}
										placeholder={`Enter ${clientType === 'SMB' ? 'username' : 'email'}...`}
										className="w-full"
										onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
									/>
									<Button
										onClick={handleSearch}
										disabled={loading || !searchQuery.trim()}
										type="primary"
									>
										<Search className="w-4 h-4" />
										Search
									</Button>
								</div>
							</div>
						</div>
					</div>

					{/* Client Details Card */}
					{selectedClient && (
						<div className="bg-white rounded-lg shadow-md p-6">
							<div className="flex justify-between items-start mb-6">
								<h2 className="text-xl font-semibold text-gray-800">Client Details</h2>
								<button
									onClick={() => setShowChangeModal(true)}
									className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
								>
									<Edit className="w-4 h-4" />
									Change Number
								</button>
							</div>

							<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
								{/* Client Info Card */}
								<div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
									<div className="space-y-3">
										<div className="flex items-center gap-2">
											<User className="w-4 h-4 text-blue-600" />
											<span className="text-sm font-medium text-gray-600">Agent:</span>
											<span className="text-sm font-semibold text-gray-800">
												{selectedClient?.number}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<Mail className="w-4 h-4 text-blue-600" />
											<span className="text-sm font-medium text-gray-600">Email:</span>
											<span className="text-sm font-semibold text-gray-800">
												{selectedClient?.email}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<Building className="w-4 h-4 text-blue-600" />
											<span className="text-sm font-medium text-gray-600">Business:</span>
											<span className="text-sm font-semibold text-gray-800">
												{selectedClient?.businessName}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<Phone className="w-4 h-4 text-blue-600" />
											<span className="text-sm font-medium text-gray-600">Current Number:</span>
											<span className="text-sm font-semibold text-blue-800 font-mono">
												{selectedClient?.currentPhoneNumber}
											</span>
										</div>
										<div className="flex items-center gap-2">
											<Headphones className="w-4 h-4 text-blue-600" />
											<span className="text-sm font-medium text-gray-600">CSM:</span>
											<span className="text-sm font-semibold text-blue-800 font-mono">
												{selectedClient?.csm}
											</span>
										</div>
									</div>
								</div>

								{/* Change History */}
								<div className="lg:col-span-2">
									<h3 className="text-lg font-medium text-gray-800 mb-4">Change History</h3>
									<div className="space-y-3 max-h-36 overflow-y-auto">
										{selectedClient?.changeHistory?.map((change) => (
											<div key={change.id} className="bg-gray-50 p-4 rounded-lg border">
												<div className="flex justify-between items-start mb-2">
													<div className="flex items-center gap-2 text-sm text-gray-600">
														<Calendar className="w-4 h-4" />
														{change?.updatedAt &&
															moment.tz(change.updatedAt, moment.tz.guess()).format('MMM D, YYYY')}
														<Clock className="w-4 h-4 ml-2" />
														{change?.updatedAt &&
															moment.tz(change.updatedAt, moment.tz.guess()).format('h:mm A')}
														<Globe className="w-4 h-4 ml-2" />
														{change?.updatedAt &&
															moment.tz(change.updatedAt, moment.tz.guess()).format('z')}
													</div>
													<span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
														Changed by: <span className="font-bold">{change?.updatedBy}</span>
													</span>
												</div>
												<div className="flex justify-between">
													<div className="text-sm">
														<span className="text-red-600 font-mono">{change?.oldNumber}</span>
														<span className="mx-2 text-gray-500">→</span>
														<span className="text-green-600 font-mono font-semibold">
															{change?.newNumber}
														</span>
													</div>
													<span className="text-xs font-medium italic">{change?.reason}</span>
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Change Number Modal */}
					{showChangeModal && (
						<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
							<div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
								<div className="flex justify-between items-center p-6 border-b">
									<h3 className="text-xl font-semibold text-gray-800">Select New Number</h3>
									<button
										onClick={() => setShowChangeModal(false)}
										className="p-2 hover:bg-gray-100 rounded-full"
									>
										<X className="w-5 h-5" />
									</button>
								</div>

								<div className="p-6">
									<div className="w-full mb-4">
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Reason to Update
										</label>
										<Input
											type="text"
											value={reason}
											onChange={(e) => setReason(e.target.value)}
											placeholder="Write Reason to update phone number (Write carefully! cant update it later.)"
											className="w-full"
										/>
									</div>
									<div className="w-full flex gap-12 items-end mb-6">
										<div className="w-full">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Country
											</label>
											<Select
												showSearch
												value={selectedCountry}
												onChange={(value) => setSelectedCountry(value)}
												className="w-full"
											>
												<Select.Option value="">Select Country</Select.Option>
												{countriesWithFlag.map((country) => (
													<Select.Option key={country.code} value={country.code}>
														{country.flag} {country.name}
													</Select.Option>
												))}
											</Select>
										</div>

										<div className="w-full">
											<label className="block text-sm font-medium text-gray-700 mb-2">
												Area Code
											</label>
											<Input
												type="text"
												value={selectedAreaCode}
												onChange={(e) => setSelectedAreaCode(e.target.value.trim())}
												disabled={!selectedCountry || loading}
												placeholder="Enter Area Code"
												className="w-full"
											/>
										</div>

										<Button
											onClick={() => getListOfNumbers()}
											disabled={loading || selectedAreaCode.length !== 3}
											type="primary"
										>
											<Search className="w-4 h-4" />
											Search
										</Button>
									</div>

									{loading && (
										<div className="text-center py-8">
											<div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
											<p className="mt-2 text-gray-600">Loading available numbers...</p>
										</div>
									)}

									{availableNumbers.length > 0 && !loading && (
										<div>
											<h4 className="text-lg font-medium text-gray-800 mb-4">
												Available Numbers ({availableNumbers.length})
											</h4>
											<div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
												{availableNumbers.map((number, index) => (
													<Popconfirm
														key={index}
														title={
															<span>
																Are you sure you want to change number from{' '}
																<strong>{selectedClient?.currentPhoneNumber}</strong> to{' '}
																<strong>{number}</strong>?
															</span>
														}
														onCancel={() => {}}
														onConfirm={() => handleNumberSelect(number)}
														okText="Update"
													>
														<button
															disabled={loading}
															className="p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-center font-mono transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
														>
															<div className="flex items-center justify-between">
																<span>{number}</span>
																<Check className="w-4 h-4 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity" />
															</div>
														</button>
													</Popconfirm>
												))}
											</div>
										</div>
									)}

									{!selectedCountry && (
										<div className="text-center py-8 text-gray-500">
											Please select a country and area code to view available numbers
										</div>
									)}
								</div>
							</div>
						</div>
					)}
				</div>
			</Spin>
		</div>
	);
};

export default AdminPhoneNumbers;
